import { Injectable } from "@angular/core";
import { LambdaRequest } from "@apis/_core/types/LambdaRequest";
import { E_Referrer } from "@backend/common/enums/referrer";
import { CompletePayload } from "@dentally/auth/auth-types";
import { AccessLevel } from "@dentally/enums/access-level";
import { PersistentObjectFactory } from "@apps/patient-core/src/app/utils/persistent-object";
import jwtDecode from "jwt-decode";
import { BehaviorSubject, filter, merge, Observable, of, Subject, take } from "rxjs";
import { CacheService } from "../cache.service";
import { BaseJWTService } from "./jwt.service";
import { WindowService } from "../window.service";
import { filterDefined } from "../../utils/rxjs";

type JWTChangedEvent = {
  status?: "UPDATED";
  jwt: CompletePayload;
};

const AUTH_DATA_KEY = "authData";
@Injectable({
  providedIn: "root",
})
export abstract class BaseAuthService implements Omit<BaseJWTService, "_internalJwtParsed" | "_jwtParsed" | "_jwtParseNoExpCheck"> {
  public onJWTChanged: BehaviorSubject<JWTChangedEvent | null> = new BehaviorSubject(null);
  public onInvalidJWT: Subject<void> = new Subject<void>();
  public onSessionIdChanged: Subject<string> = new Subject<string>();
  private _persistentObjectFactory: PersistentObjectFactory<CompletePayload>;
  private _authData: CompletePayload;
  protected _accessToken: string;

  constructor(protected _cacheService: CacheService, protected _windowService: WindowService) {
    this._persistentObjectFactory = new PersistentObjectFactory(this._cacheService, AUTH_DATA_KEY, {
      get: (): CompletePayload | null => {
        let authData = this._cacheService.getJson(AUTH_DATA_KEY);

        if (!authData) {
          authData = this._cacheService.getJsonSession(AUTH_DATA_KEY);
        }

        return authData as CompletePayload | null;
      },
      set: (value: object) => {
        this._cacheService.setJson(AUTH_DATA_KEY, value);
      },
      delete: () => {
        this._cacheService.delete(AUTH_DATA_KEY);
      },
    });
    this._persistentObjectFactory.ttlDuration = undefined; // So that it will expire when the session ends
    this._authData = this._persistentObjectFactory.create({
      access_level: AccessLevel.PUBLIC,
    } as unknown as CompletePayload);
  }

  public abstract handleInvalidToken(reload?: boolean): null;

  public abstract get isRestricted(): boolean;

  public abstract get jwtExpiryFormatted(): string | null;

  public abstract get jwtCreatedAtFormatted(): string | null;

  public init(): Promise<void> {
    return Promise.resolve(); // Only used for Concierge
  }

  public get accessLevel(): AccessLevel {
    return this._authData.access_level;
  }

  public set accessToken(accessToken: string) {
    this._accessToken = accessToken;
  }

  public get hasExpired(): boolean {
    return false; // We'll rely on the backend JWT authorizer to return a 403 to tell us this via handleInvalidToken
  }

  public get referrerAsJson(): { type: E_Referrer } {
    const { referrer: referrerJSON } = this._authData;

    return referrerJSON ? JSON.parse(referrerJSON) : { type: E_Referrer.ORGANIC };
  }

  public get sid(): string {
    return this._authData.sid;
  }

  public isConcierge(): boolean {
    return LambdaRequest.conciergeAccessLevels.includes(this.accessLevel);
  }

  public isPatientInPractice() {
    return this.accessLevel === AccessLevel.PATIENT_IN_PRACTICE;
  }

  public isPublicInPractice(accessLevel: AccessLevel): boolean {
    return accessLevel === AccessLevel.PUBLIC_IN_PRACTICE;
  }

  public isPublicAccess() {
    return LambdaRequest.publicAccessLevels.includes(this.accessLevel);
  }

  public isPatientUnauthenticated() {
    return [AccessLevel.PATIENT_UNAUTHENTICATED, AccessLevel.PATIENT_IN_PRACTICE].includes(this.accessLevel);
  }

  public isPublic() {
    return this.accessLevel === AccessLevel.PUBLIC;
  }

  public isImpersonating() {
    return this._authData.user_impersonating_patient !== undefined;
  }

  public isLoggedIn() {
    return this.accessLevel === AccessLevel.PATIENT;
  }

  public restorePublicInPracticeToken() {
    // Clear the access token so that the next API call will retrieve a new one using the refresh token
    this._accessToken = "";
  }

  public isPatient() {
    return this.isLoggedIn() || this.isPatientUnauthenticated();
  }

  public get canLogin(): boolean {
    if (this._authData.can_login === undefined) {
      return true;
    }

    return !["false", false].includes(this._authData.can_login);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public getFirstName(): any {
    const { patient_first_name } = this._authData;

    if (patient_first_name) {
      return `${patient_first_name}'s`;
    }

    return "Your";
  }

  public getJwtField<T = string>(field: string): T | null {
    return this._authData[field] ?? null;
  }

  public getJwtSafe(): Record<string, string> | null {
    return this._authData;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public getJWT(field?: string | null, _reloadOnExpired = true): any {
    if (field) {
      return this._authData?.[field] ?? null;
    }

    return this._authData;
  }

  public getJWTString(): string {
    return this._accessToken;
  }

  public delete() {
    this._persistentObjectFactory.clear(this._authData);
    this._accessToken = "";
  }

  // #region Set Token
  public setPublicToken(accessToken: string, forceSwap = false): void {
    if (forceSwap || !this._accessToken) {
      this.setToken(accessToken);
    }
  }

  public setToken(accessToken: string): void {
    this._accessToken = accessToken;

    const authData = jwtDecode<CompletePayload>(accessToken);

    Object.assign(this._authData, authData);

    for (const key in this._authData) {
      if (key in authData === false) {
        delete this._authData[key];
      }
    }

    this._persistentObjectFactory.save(this._authData);

    this.onJWTChanged.next({
      status: "UPDATED",
      jwt: this._authData,
    });
  }
  // #endregion

  /**
   * Waits for the JWT to be available so that API calls that require it can be delayed
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public waitForJwt(): Observable<any> {
    return merge(this.onJWTChanged, of({ jwt: this._authData } as JWTChangedEvent)).pipe(
      filterDefined(),
      filter((status) => !!status.jwt),
      take(1)
    );
  }

  public get hasNoJwt(): boolean {
    return !this._accessToken;
  }

  public getAuthToken(): Observable<string | null> {
    return of(this._accessToken);
  }
}
